// v2 zebra — stripes built as alternate banded parts: thin ellipsoid slices that
// wrap the barrel's curve, plus angled haunch and shoulder bands, two bands
// ringing the raised neck, an upright two-segment brush mane, a proper horse
// build (chest and haunch masses, long head with a black muzzle, pricked ears)
// and a black-tufted tail. Two coats: crisp white with ink-black stripes and a
// dusty plains form. Ported 1:1 from age-of-mcp's v2/zebra.ts, using the
// library's shared `critter` (quadLegs) and `safari` (bands, eyes, tube)
// helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { coat: "#f0efe8", stripe: "#23211f", hoof: "#3a3632" },
  { coat: "#e6e0d2", stripe: "#3a2e26", hoof: "#443830" },
];

function zebraParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.075, dz: 0.033, bodyY: 0.14, rTop: 0.009, rFoot: 0.0065,
    color: c.coat, hoof: { color: c.hoof, r: 0.0078, h: 0.012 },
  });
  parts.push(
    // barrel with chest and haunch masses
    { geometry: new THREE.IcosahedronGeometry(0.054, 0), color: c.coat,
      position: [0, 0.14, 0], scale: [1.85, 1.0, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: c.coat,
      position: [0.078, 0.142, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.043, 0), color: c.coat,
      position: [-0.085, 0.142, 0] },
    // angled shoulder band + two angled haunch bands
    { geometry: new THREE.IcosahedronGeometry(0.0405, 0), color: c.stripe,
      position: [0.07, 0.145, 0], rotation: [0, 0, -0.4],
      scale: [0.15, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.0405, 0), color: c.stripe,
      position: [-0.088, 0.146, 0], rotation: [0, 0, 0.55],
      scale: [0.15, 0.95, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.037, 0), color: c.stripe,
      position: [-0.104, 0.142, 0], rotation: [0, 0, 0.7],
      scale: [0.15, 0.9, 0.92] },
    // raised neck ringed by two stripe bands (thin axis along the neck)
    safari.tube([0.098, 0.158, 0], [0.142, 0.218, 0], 0.0295, 0.022, c.coat, 5),
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.stripe,
      position: [0.115, 0.181, 0], rotation: [0, 0, 0.94],
      scale: [0.18, 1.0, 0.82] },
    { geometry: new THREE.IcosahedronGeometry(0.0265, 0), color: c.stripe,
      position: [0.13, 0.201, 0], rotation: [0, 0, 0.94],
      scale: [0.18, 1.0, 0.82] },
    // upright two-segment brush mane along the crest
    { geometry: new THREE.BoxGeometry(0.013, 0.052, 0.014), color: c.stripe,
      position: [0.094, 0.196, 0], rotation: [0, 0, -0.63] },
    { geometry: new THREE.BoxGeometry(0.012, 0.046, 0.013), color: c.stripe,
      position: [0.112, 0.218, 0], rotation: [0, 0, -0.63] },
    // long head with a black muzzle and pricked ears
    { geometry: new THREE.IcosahedronGeometry(0.0255, 0), color: c.coat,
      position: [0.163, 0.224, 0], scale: [1.5, 0.95, 0.82] },
    { geometry: new THREE.IcosahedronGeometry(0.0135, 0), color: c.stripe,
      position: [0.196, 0.216, 0], scale: [0.95, 0.8, 0.78] },
    { geometry: new THREE.ConeGeometry(0.0075, 0.021, 4), color: c.coat,
      position: [0.152, 0.242, 0.0125], rotation: [0.4, 0, -0.05] },
    { geometry: new THREE.ConeGeometry(0.0075, 0.021, 4), color: c.coat,
      position: [0.152, 0.242, -0.0125], rotation: [-0.4, 0, -0.05] },
    // tail ending in the black tuft
    safari.tube([-0.102, 0.148, 0], [-0.128, 0.075, 0.004], 0.0048, 0.003, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: c.stripe,
      position: [-0.131, 0.066, 0.005] },
  );
  // the body stripes — five slices wrapped over the barrel
  parts.push(...safari.bands({
    xs: [-0.062, -0.032, -0.002, 0.028, 0.056], y: 0.14, r: 0.0545,
    scale: [0.16, 1.0, 0.87], color: c.stripe,
  }));
  parts.push(...safari.eyes(0.172, 0.2335, 0.0135, 0.004, "#16130e"));
  return parts;
}

export const zebraV2 = defineModel({
  id: "zebra-v2",
  scale: 2.18, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Zebra (v2)",
  tags: ["creature","zebra","low-poly","age-of-mcp","fable"],
  summary: "Low-poly zebra.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.15,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => zebraParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => zebraParts(1) },
  ],
  animations: [],
});
